import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useConversations } from '../hooks/useConversations';
import { useContact } from '../hooks/useContact';
import ConversationList from '../components/conversations/ConversationList';
import MessageThread from '../components/conversations/MessageThread';
import ContactContextPanel from '../components/conversations/ContactContextPanel';
import OpportunityDrawer from '../components/opportunities/OpportunityDrawer';
import type { Conversation } from '../components/conversations/types';
import { api } from '../lib/api';

/** TODO1: where a to-do's "Draft message" link stands while its contact's thread is found, or when there is none. */
type LinkNote = 'finding' | 'none' | 'failed';
const NO_THREAD = 'No thread yet - open in GoHighLevel';

export default function Conversations() {
  const c = useConversations();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showContext, setShowContext] = useState(false);
  const [chanReq, setChanReq] = useState<{ ch: 'sms' | 'email'; n: number } | null>(null);
  const [dealDrawerId, setDealDrawerId] = useState<string | null>(null);

  // TODO1 (Task 9): /crm/conversations?contact=<ghl contact id>&todo=<task id> - a to-do's "Draft message". The thread is
  // found by its contact (it may be far down the list), and its composer drafts ONCE for the to-do.
  const [params] = useSearchParams();
  const linkContact = params.get('contact');
  const [linked, setLinked] = useState<Conversation | null>(null);
  const [linkNote, setLinkNote] = useState<LinkNote | null>(null);
  // Cleared when he opens another thread, so going back never drafts again.
  const [draftTodo, setDraftTodo] = useState<string | null>(params.get('todo'));
  const [findTries, setFindTries] = useState(0);
  useEffect(() => {
    if (!linkContact) return;
    let live = true;
    setLinkNote('finding');
    api.listConversations({ contact_id: linkContact, limit: '1' })
      .then((res: { conversations?: Conversation[] }) => {
        if (!live) return;
        const found = res?.conversations?.[0] ?? null;
        if (!found) { setLinkNote('none'); return; }
        setLinked(found);
        setSelectedId(found.id);
        setLinkNote(null);
      })
      // ⛔ A failed look-up is never "no thread": it says so, with a retry.
      .catch(() => { if (live) setLinkNote('failed'); });
    return () => { live = false; };
  }, [linkContact, findTries]);

  const selected = c.conversations.find((x) => x.id === selectedId) || (linked?.id === selectedId ? linked : null);
  // With no thread, the contact is still read: the panel shows him, and his GoHighLevel link comes from it.
  const { contact, loading: contactLoading } = useContact(
    selected?.contact_id ?? (linkNote === 'none' ? linkContact : null),
  );
  const threadOpen = selectedId !== null || linkNote !== null;

  const requestChannel = (ch: 'sms' | 'email') => setChanReq((r) => ({ ch, n: (r?.n || 0) + 1 }));

  return (
    <Layout>
      <div className="h-[calc(var(--vh-full)-8rem-var(--tab-bar-space,0px))] min-h-[520px] border border-gray-200 rounded-lg overflow-hidden bg-white">
        {/* CONV-LAYOUT (the 144th): three columns only from xl. At lg the list (340) and the contact column (320) left the
            thread 64 px at a 1024 px window; between 1024 and 1279 the contact opens from Details instead. */}
        <div className="grid grid-cols-1 lg:grid-cols-[340px_minmax(0,1fr)] xl:grid-cols-[340px_minmax(0,1fr)_320px] h-full min-h-0">
          {/* List pane */}
          <div className={`border-r border-gray-200 min-h-0 ${threadOpen ? 'hidden lg:block' : 'block'} h-full`}>
            <ConversationList
              conversations={c.conversations}
              loading={c.loading}
              loadingMore={c.loadingMore}
              error={c.error}
              total={c.total}
              channel={c.channel}
              unread={c.unread}
              owner={c.owner}
              brokers={c.brokers}
              query={c.query}
              onChannel={c.setChannel}
              onUnread={c.setUnread}
              onOwner={c.setOwner}
              onQuery={c.setQuery}
              selectedId={selectedId}
              onSelect={(id) => {
                setSelectedId(id);
                setShowContext(false);
                setChanReq(null); // a channel picked on the last thread must not open the next one's composer on it
                setLinkNote(null);
                setDraftTodo(null);
              }}
              onLoadMore={c.loadMore}
              hasMore={c.hasMore}
              onRefresh={c.refresh}
              refreshing={c.refreshing}
            />
          </div>

          {/* Thread pane */}
          <div className={`min-h-0 h-full ${threadOpen ? 'block' : 'hidden lg:block'}`}>
            {!selected && linkNote ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center text-sm">
                {linkNote === 'finding' && <p className="text-gray-400">Finding the thread...</p>}
                {linkNote === 'none' && (contact?.ghl_contact_url ? (
                  <a href={contact.ghl_contact_url} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">
                    {NO_THREAD}
                  </a>
                ) : (
                  <p className="text-gray-600">{NO_THREAD}</p>
                ))}
                {linkNote === 'failed' && (
                  <>
                    <p role="alert" className="text-red-600">Couldn't find this contact's thread - try again.</p>
                    <button type="button" onClick={() => setFindTries((n) => n + 1)} className="text-xs px-3 py-1 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                      Try again
                    </button>
                  </>
                )}
                <button type="button" onClick={() => setLinkNote(null)} className="lg:hidden text-xs text-gray-500 hover:text-gray-700">
                  Back to conversations
                </button>
              </div>
            ) : (
              /* I1 (CONV2's review): one thread PER conversation. A single instance let a reply that finished after he
                 opened another thread land the old thread's messages on it, and kept a draft typed for one contact in
                 the box for the next. */
              <MessageThread
                key={selected?.id ?? 'none'}
                conversation={selected}
                contact={contact}
                requestedChannel={chanReq}
                autoDraftTodo={selected && selected.id === linked?.id ? draftTodo ?? undefined : undefined}
                onBack={() => setSelectedId(null)}
                onToggleContext={() => setShowContext((v) => !v)}
              />
            )}
          </div>

          {/* Context pane (inline from xl) */}
          <div className="hidden xl:block border-l border-gray-200 min-h-0 h-full overflow-y-auto">
            <ContactContextPanel
              contact={contact}
              loading={contactLoading}
              conversationId={selectedId}
              onSelectChannel={requestChannel}
              onOpenDeal={setDealDrawerId}
            />
          </div>
        </div>
      </div>

      {/* Context drawer for < xl. z-50, a page dialog's layer, above the phone bar's z-40 (CHAT-DUET review). */}
      {showContext && selected && (
        <div className="fixed inset-0 z-50 flex justify-end xl:hidden" onClick={() => setShowContext(false)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative w-[340px] max-w-full h-full bg-white shadow-xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <ContactContextPanel
              contact={contact}
              loading={contactLoading}
              conversationId={selectedId}
              onSelectChannel={(ch) => {
                requestChannel(ch);
                setShowContext(false);
              }}
              onOpenDeal={(id) => {
                setDealDrawerId(id);
                setShowContext(false);
              }}
              onClose={() => setShowContext(false)}
            />
          </div>
        </div>
      )}

      {dealDrawerId && (
        <OpportunityDrawer id={dealDrawerId} onClose={() => setDealDrawerId(null)} onSaved={() => {}} />
      )}
    </Layout>
  );
}
